// ============================================
// Player Booking Page
// ============================================

import React from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event, EventTherapist, BookingRequest, Session, STATUS_COLORS } from '../../types';

interface ContextType {
  event: Event;
}

export default function PlayerBook() {
  const { event } = useOutletContext<ContextType>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [therapists, setTherapists] = React.useState<EventTherapist[]>([]);
  const [existingBooking, setExistingBooking] = React.useState<BookingRequest | null>(null);
  const [existingSession, setExistingSession] = React.useState<Session | null>(null);
  const [pendingFeedbackSession, setPendingFeedbackSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [checkingBookings, setCheckingBookings] = React.useState(true);

  // Form state
  const [tableNo, setTableNo] = React.useState<number | null>(null);
  const [seatNo, setSeatNo] = React.useState<number | null>(null);
  const [minutes, setMinutes] = React.useState<number>(event.defaultBookingMinutes || 30);
  const [therapistChoice, setTherapistChoice] = React.useState<'random' | string>('random');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load therapists
  React.useEffect(() => {
    const q = query(
      collection(db, 'events', event.id, 'therapists'),
      where('dutyStatus', '==', 'on_duty')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      })) as EventTherapist[];
      setTherapists(data);
      setLoading(false);
    });

    return unsubscribe;
  }, [event.id]);

  // Check for existing booking or session
  React.useEffect(() => {
    if (!user) {
      setCheckingBookings(false);
      return;
    }

    let bookingChecked = false;
    let sessionChecked = false;
    let feedbackChecked = false;

    const checkDone = () => {
      if (bookingChecked && sessionChecked && feedbackChecked) {
        setCheckingBookings(false);
      }
    };

    // Check for existing bookings
    const bookingQuery = query(
      collection(db, 'events', event.id, 'bookingRequests'),
      where('playerUid', '==', user.uid),
      where('status', 'in', ['pending', 'assigned', 'accepted', 'started'])
    );

    const unsubBooking = onSnapshot(bookingQuery, (snapshot) => {
      if (!snapshot.empty) {
        const booking = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as BookingRequest;
        setExistingBooking(booking);
      } else {
        setExistingBooking(null);
      }
      bookingChecked = true;
      checkDone();
    });

    // Check for existing sessions
    const sessionQuery = query(
      collection(db, 'events', event.id, 'sessions'),
      where('playerUid', '==', user.uid),
      where('status', '==', 'active')
    );

    const unsubSession = onSnapshot(sessionQuery, (snapshot) => {
      if (!snapshot.empty) {
        const session = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Session;
        setExistingSession(session);
      } else {
        setExistingSession(null);
      }
      sessionChecked = true;
      checkDone();
    });

    // Check for completed sessions needing feedback (if feedback is enabled)
    const needsFeedback = event.askCustomerAmount || event.askCustomerRating;
    if (needsFeedback) {
      // Get most recent completed session without feedback
      const feedbackQuery = query(
        collection(db, 'events', event.id, 'sessions'),
        where('playerUid', '==', user.uid),
        where('status', '==', 'completed'),
        orderBy('completedAt', 'desc'),
        limit(1)
      );

      const unsubFeedback = onSnapshot(feedbackQuery, (snapshot) => {
        if (!snapshot.empty) {
          const session = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Session;
          // Only prompt for feedback if no feedback has been given yet
          // and session was completed within the last 24 hours
          const completedAt = session.completedAt?.toDate();
          const hoursSinceCompletion = completedAt
            ? (Date.now() - completedAt.getTime()) / (1000 * 60 * 60)
            : 999;

          if (!session.customerFeedbackAt && hoursSinceCompletion < 24) {
            setPendingFeedbackSession(session);
          } else {
            setPendingFeedbackSession(null);
          }
        } else {
          setPendingFeedbackSession(null);
        }
        feedbackChecked = true;
        checkDone();
      });

      return () => {
        unsubBooking();
        unsubSession();
        unsubFeedback();
      };
    } else {
      feedbackChecked = true;
      checkDone();
      return () => {
        unsubBooking();
        unsubSession();
      };
    }
  }, [event.id, event.askCustomerAmount, event.askCustomerRating, user]);

  // If there's an existing booking or session, redirect to status
  React.useEffect(() => {
    if (existingBooking || existingSession) {
      navigate('status');
    }
  }, [existingBooking, existingSession, navigate]);

  // If there's a session needing feedback, redirect to feedback page
  React.useEffect(() => {
    if (pendingFeedbackSession && !existingBooking && !existingSession) {
      navigate(`feedback/${pendingFeedbackSession.id}`);
    }
  }, [pendingFeedbackSession, existingBooking, existingSession, navigate]);

  // Update minutes when therapist selection changes
  React.useEffect(() => {
    if (therapistChoice === 'random') {
      // Use event default
      setMinutes(event.defaultBookingMinutes || 30);
    } else {
      // Find selected therapist and use their default if set
      const selectedTherapist = therapists.find(t => t.uid === therapistChoice);
      if (selectedTherapist?.defaultBookingMinutes) {
        setMinutes(selectedTherapist.defaultBookingMinutes);
      } else {
        // Fall back to event default
        setMinutes(event.defaultBookingMinutes || 30);
      }
    }
  }, [therapistChoice, therapists, event.defaultBookingMinutes]);

  // Duration adjustment
  const adjustMinutes = (delta: number) => {
    const newMinutes = minutes + delta;
    // Clamp between min and max from event options
    const minOption = Math.min(...event.bookingMinuteOptions);
    const maxOption = Math.max(...event.bookingMinuteOptions);
    if (newMinutes >= minOption && newMinutes <= maxOption) {
      setMinutes(newMinutes);
    }
  };

  const handleSubmit = async () => {
    if (!tableNo || !seatNo || !minutes) {
      setError('Please select table, seat, and duration');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const createBookingRequest = httpsCallable(functions, 'createBookingRequest');
      await createBookingRequest({
        eventId: event.id,
        tableNo,
        seatNo,
        minutes,
        therapistChoice: therapistChoice === 'random' ? 'random' : 'specific',
        preferredTherapistId: therapistChoice === 'random' ? null : therapistChoice,
      });

      navigate('status');
    } catch (err: any) {
      console.error('Booking error:', err);
      setError(err.message || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking for existing bookings/sessions
  if (loading || checkingBookings) {
    return (
      <div className="player-book loading">
        <div className="spinner"></div>
      </div>
    );
  }

  // Don't show the form if player has existing booking/session (will redirect)
  if (existingBooking || existingSession) {
    return (
      <div className="player-book loading">
        <div className="spinner"></div>
        <p>Redirecting to your booking...</p>
      </div>
    );
  }

  // Check if any therapists are available
  const availableTherapists = therapists.filter((t) => t.uiStatus === 'green');
  const minOption = Math.min(...event.bookingMinuteOptions);
  const maxOption = Math.max(...event.bookingMinuteOptions);

  return (
    <div className="player-book">
      <h2>Book a Massage</h2>

      {error && <div className="error-message">{error}</div>}

      {availableTherapists.length === 0 && (
        <div className="warning-message">
          No therapists currently available. You can still book and wait.
        </div>
      )}

      {/* Therapist Selection - First */}
      <section className="booking-section">
        <h3>Choose Therapist</h3>
        <div className="therapist-selection-grid">
          <button
            className={`therapist-card-option ${therapistChoice === 'random' ? 'selected' : ''}`}
            onClick={() => setTherapistChoice('random')}
          >
            <div className="therapist-photo-tall placeholder random-icon">?</div>
            <span className="therapist-name">Any Available</span>
          </button>

          {therapists.map((therapist) => (
            <button
              key={therapist.uid}
              className={`therapist-card-option ${therapistChoice === therapist.uid ? 'selected' : ''}`}
              onClick={() => setTherapistChoice(therapist.uid)}
            >
              <div className="therapist-photo-wrapper">
                {therapist.photoUrl && therapist.photoStatus === 'approved' ? (
                  <img
                    src={therapist.photoUrl}
                    alt={therapist.displayName}
                    className="therapist-photo-tall"
                  />
                ) : (
                  <div className="therapist-photo-tall placeholder">
                    {(therapist.publicDisplayName || therapist.displayName).charAt(0)}
                  </div>
                )}
                <span
                  className="status-indicator-dot"
                  style={{ backgroundColor: STATUS_COLORS[therapist.uiStatus] }}
                />
              </div>
              <span className="therapist-name">{therapist.publicDisplayName || therapist.displayName}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Location Selection */}
      <section className="booking-section">
        <h3>Your Location</h3>
        <div className="location-row">
          <div className="location-field">
            <label>Table</label>
            <select
              value={tableNo || ''}
              onChange={(e) => setTableNo(e.target.value ? Number(e.target.value) : null)}
              className="table-select"
            >
              <option value="">Select...</option>
              {Array.from({ length: event.tablesCount }, (_, i) => i + 1).map((num) => (
                <option key={num} value={num}>
                  Table {num}
                </option>
              ))}
            </select>
          </div>

          <div className="location-field">
            <label>Seat</label>
            <div className="seat-buttons">
              {Array.from({ length: event.seatsPerTable }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  className={`seat-btn ${seatNo === num ? 'selected' : ''}`}
                  onClick={() => setSeatNo(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Duration Selection */}
      <section className="booking-section">
        <h3>Duration</h3>
        <div className="duration-stepper">
          <button
            className="stepper-btn"
            onClick={() => adjustMinutes(-15)}
            disabled={minutes <= minOption}
          >
            -15
          </button>
          <div className="duration-display">
            <span className="duration-value">{minutes}</span>
            <span className="duration-unit">min</span>
            <span className="duration-price">
              {event.currency} {(minutes * event.pricePerMinute).toFixed(0)}
            </span>
          </div>
          <button
            className="stepper-btn"
            onClick={() => adjustMinutes(15)}
            disabled={minutes >= maxOption}
          >
            +15
          </button>
        </div>
      </section>

      {/* Summary & Book Button */}
      <section className="booking-summary">
        {tableNo && seatNo && (
          <div className="summary-text">
            Table {tableNo}, Seat {seatNo} - {minutes} min -{' '}
            {event.currency} {(minutes * event.pricePerMinute).toFixed(0)}
          </div>
        )}

        <button
          className="btn btn-primary btn-xlarge book-btn"
          onClick={handleSubmit}
          disabled={!tableNo || !seatNo || !minutes || submitting}
        >
          {submitting ? 'Booking...' : 'Book Now'}
        </button>
      </section>
    </div>
  );
}
